import type { FC } from 'react';
import type { TFunction } from 'i18next';
import type { Control, FieldArrayWithId, FieldErrors } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import { Button, Flex, Form, Select } from 'antd';
import TextArea from 'antd/es/input/TextArea';

import type { UpdateFormValues } from '@modules/ai-agent/lib/types';
import { Text } from '@shared/ui/antd';
import { useToggle } from '@shared/hooks/use-toggle';
import { CreateAiAgentModal } from '@shared/ui/sidebar/sidebar-primary-navigation/ui/create-ai-agent-modal/createa-ai-agent-modal';

import { DeleteOutlinedSmall, PlusBackground } from '@shared/assets/icons/components';

import styles from '../../prompt.module.scss';

interface RelatedAgentsProps {
  agentLinkFields: FieldArrayWithId<UpdateFormValues, 'agent_links', 'id'>[];
  control: Control<UpdateFormValues>;
  errors: FieldErrors<UpdateFormValues>;
  isLoading: boolean;
  promptPlaceholders: string[];
  agentOptions: { value: number; label: string }[];
  onAddAgentLink: () => void;
  onRemoveAgentLink: (index: number) => void;
  t: TFunction<'translation', 'prompt'>;
}

export const RelatedAgents: FC<RelatedAgentsProps> = ({
  agentLinkFields,
  control,
  errors,
  isLoading,
  promptPlaceholders,
  agentOptions,
  onAddAgentLink,
  onRemoveAgentLink,
  t,
}) => {
  const {
    state: isCreateAiAgentModalOpen,
    setTrue: openCreateAiAgentModal,
    setFalse: closeCreateAiAgentModal,
  } = useToggle();
  const watchedLinks = useWatch({ control, name: 'agent_links' }) ?? [];
  const selectedTargets = watchedLinks.map((item) => Number(item?.target_agent_id ?? 0));
  const shouldShowEmptyState = agentOptions.length === 0;

  return (
    <Flex vertical className={styles.element} gap={16}>
      <Flex gap={4} align="start" vertical>
        <Text variant="md" fontWeight={500}>
          {t('linkedAgents.title')}
        </Text>

        <Text variant="sm" style={{ color: 'var(--color-text-tertiary)' }}>
          {t('linkedAgents.description')}
        </Text>
      </Flex>

      {shouldShowEmptyState ? (
        <Flex
          vertical
          align="center"
          justify="center"
          gap={8}
          className={styles.relatedAgentsEmpty}
        >
          <Text variant="md" fontWeight={500} className={styles.relatedAgentsEmptyTitle}>
            {t('linkedAgents.empty.title')}
          </Text>
          <Text variant="sm" className={styles.relatedAgentsEmptyDescription}>
            {t('linkedAgents.empty.description')}
          </Text>
          <Button type="default" size="large" onClick={openCreateAiAgentModal}>
            {t('linkedAgents.empty.action')}
          </Button>
        </Flex>
      ) : (
        <Flex vertical gap={12} className={styles.element_inside}>
          <Flex vertical gap={12} style={{ width: '100%' }}>
            {agentLinkFields.map((field, index) => (
              <Flex key={field.id} gap={12} className={styles.stageRow}>
                <div className={styles.stageRowContent}>
                  <div className={styles.stagePrimaryCol}>
                    <Text variant="sm" fontWeight={400} className={styles.stageLabel}>
                      {index + 1}. {t('linkedAgents.linkLabel')}
                    </Text>

                    <Form.Item
                      help={errors.agent_links?.[index]?.prompt?.message}
                      validateStatus={errors.agent_links?.[index]?.prompt ? 'error' : ''}
                      className={styles.label}
                    >
                      <Controller
                        control={control}
                        name={`agent_links.${index}.prompt`}
                        render={({ field }) => (
                          <TextArea
                            {...field}
                            id={`agent-links-prompt-${index}`}
                            placeholder={promptPlaceholders[index] ?? promptPlaceholders.at(-1)}
                            className={styles.stageArea}
                            style={{ resize: 'none' }}
                            autoSize={{ minRows: 2 }}
                            disabled={isLoading}
                          />
                        )}
                      />
                    </Form.Item>
                  </div>

                  <div className={styles.stageSecondaryCol}>
                    <Text variant="sm" fontWeight={500} className={styles.stageFieldLabel}>
                      {t('linkedAgents.targetAgentLabel')}
                    </Text>

                    <Form.Item
                      help={errors.agent_links?.[index]?.target_agent_id?.message}
                      validateStatus={errors.agent_links?.[index]?.target_agent_id ? 'error' : ''}
                      className={styles.stageFieldItem}
                    >
                      <Controller
                        control={control}
                        name={`agent_links.${index}.target_agent_id`}
                        render={({ field }) => {
                          const normalizedValue = Number(field.value);
                          const options = agentOptions.filter(
                            (option) =>
                              option.value === normalizedValue ||
                              !selectedTargets.some(
                                (targetId, targetIndex) =>
                                  targetIndex !== index && targetId === option.value,
                              ),
                          );

                          return (
                            <Select
                              value={
                                Number.isFinite(normalizedValue) && normalizedValue > 0
                                  ? normalizedValue
                                  : undefined
                              }
                              onChange={(val) => field.onChange(Number(val))}
                              options={options}
                              className={styles.stageFieldSelect}
                              disabled={isLoading}
                              showSearch
                              optionFilterProp="label"
                              placeholder={t('linkedAgents.selectAgent')}
                            />
                          );
                        }}
                      />
                    </Form.Item>
                  </div>
                </div>

                <Flex gap={8} className={styles.stageActions}>
                  <button
                    type="button"
                    className={styles.stageActionButton}
                    onClick={() => onRemoveAgentLink(index)}
                    aria-label="Remove linked agent"
                  >
                    <DeleteOutlinedSmall color="#A7A8AE" />
                  </button>
                </Flex>
              </Flex>
            ))}
          </Flex>

          <Button
            icon={<PlusBackground fill="var(--color-text-secondary)" />}
            iconPosition="end"
            onClick={onAddAgentLink}
            type="text"
            className={styles.button_additional}
          >
            {t('linkedAgents.addButton')}
          </Button>
        </Flex>
      )}

      <CreateAiAgentModal
        isOpen={isCreateAiAgentModalOpen}
        onClose={closeCreateAiAgentModal}
        shouldNavigateToCreatedAgent={false}
      />
    </Flex>
  );
};
